import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useGlobalModal } from "../../../../contexts/GlobalModalContext";
import { PrimaryButtonStyled } from "../../PrimaryButtonStyled";
import { OutlinedButtonStyled } from "../../OutlinedButtonStyled";
import { moveStatus } from "../../../../api/RosterApi";
import { useAlert } from "../../../../contexts/AlertsContext";
import {
    getCompleteConsentFormPdf,
    getJurisdictionAndStatus,
    getPersonnelDisclosureReview,
} from "../../../../api/PersonnelApi";
import { Col, Container, Row, Tabs, Tab } from "react-bootstrap";
import DisclosureTab from "./DisclosureTab";
import ReviewTab from "./ReviewTab";

const BodyContent = styled.div`
    text-align: left;
    padding-top: 0.7rem;
    height: 445px;
    overflow-y: scroll;
    color: var(--slate);
    font-size: 12px;
    background-color: var(--cloud);
    & .kvwlRj {
        padding: 0rem !important;
    }
`;

const NameContent = styled.div`
    font-size: 16px;
    color: var(--slate);
`;

const TitleContent = styled.div`
    font-size: 12px;
    color: var(--slate);
`;
const OrganizationContent = styled.div`
    font-size: 12px;
    color: var(--slate);
`;

const StyledIcon = styled.i.attrs(() => ({
    className: "fa fa-check-circle ml-1",
}))`
    color: green;
    font-size: 14px;
`;

const DISCLOSURE_REVIEW_POLL_ATTEMPTS = 10;
const DISCLOSURE_REVIEW_POLL_INTERVAL_MS = 400;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

type ReviewDisclosureModalBodyProps = {
    personnel: any;
    disclosureComment: string;
    setDisclosureComment: Function;
    reviewComment: string;
    setReviewComment: Function;
    disclosureReviews: any[];
    setDisclosureReviews: Function;
    currentJurisdiction: any;
    currentJurisdictionIndex: number;
    setCurrentJurisdictionIndex: any;
    setCurrentJurisdiction: Function;
    targetJurisdiction: string;
    callback: Function;
    active: string;
};

const ReviewDisclosureModalBody: React.FC<ReviewDisclosureModalBodyProps> = ({
    personnel,
    disclosureComment,
    setDisclosureComment,
    reviewComment,
    setReviewComment,
    setDisclosureReviews,
    currentJurisdiction,
    currentJurisdictionIndex,
    setCurrentJurisdictionIndex,
    setCurrentJurisdiction,
    targetJurisdiction,
    disclosureReviews,
    active,
    callback,
}) => {
    const DISCLOSURE_TAB = "disclosure";
    const REVIEW_TAB = "reviewConsent";
    const { dispatch } = useGlobalModal();
    const [disclosureCount, setDisclosureCount] = useState(0);
    const [reviewCount, setReviewCount] = useState(0);
    const [activeTab, setActiveTab] = useState(DISCLOSURE_TAB);
    const [jurisdictionInfo, setJurisdictionInfo] = useState(null);
    const showAlert = useAlert();
    const readOnly = active && active.toUpperCase() === "INACTIVE" ? true : false;

    const normalizeDisclosureReviewData = (data: any[]) => {
        const ordered = [...data];
        if (active) {
            return ordered.filter((item: any) => item.jurisdiction === targetJurisdiction);
        }
        ordered.forEach((item: any, i: number) => {
            if (item.jurisdiction === targetJurisdiction) {
                ordered.splice(i, 1);
                ordered.unshift(item);
            }
        });
        return ordered;
    };

    const applyDisclosureReviewData = (raw: any[], preserveJurisdiction?: string) => {
        const data = normalizeDisclosureReviewData(raw);
        setDisclosureReviews(data);
        const jurisdictionKey =
            preserveJurisdiction ?? currentJurisdiction?.jurisdiction ?? data[0]?.jurisdiction;
        const idx = data.findIndex((item: any) => item.jurisdiction === jurisdictionKey);
        const index = idx >= 0 ? idx : 0;
        if (data.length > 0) {
            setCurrentJurisdiction(data[index]);
            setCurrentJurisdictionIndex(index);
        }
        return { data, jurisdictionKey };
    };

    const reloadDisclosureReviews = (preserveJurisdiction?: string) => {
        return getPersonnelDisclosureReview(personnel.id).then(raw =>
            applyDisclosureReviewData(raw, preserveJurisdiction),
        );
    };

    const getJurisdictionFromReload = (result: {
        data: any[];
        jurisdictionKey: string;
    }): any | undefined => {
        const idx = result.data.findIndex(
            (item: any) => item.jurisdiction === result.jurisdictionKey,
        );
        return idx >= 0 ? result.data[idx] : result.data[0];
    };

    const reloadDisclosureReviewsUntilSettled = async (
        preserveJurisdiction: string,
        pendingField: "disclosurePending" | "reviewPending",
    ) => {
        for (let attempt = 0; attempt < DISCLOSURE_REVIEW_POLL_ATTEMPTS; attempt++) {
            const result = await reloadDisclosureReviews(preserveJurisdiction);
            const jurisdiction = getJurisdictionFromReload(result);
            if (!jurisdiction?.[pendingField]) {
                return result;
            }
            if (attempt < DISCLOSURE_REVIEW_POLL_ATTEMPTS - 1) {
                await delay(DISCLOSURE_REVIEW_POLL_INTERVAL_MS);
            }
        }
        showAlert(
            "Status is still updating. Close and reopen the modal if the screen does not refresh.",
            "danger",
        );
        return reloadDisclosureReviews(preserveJurisdiction);
    };

    useEffect(() => {
        if (personnel) {
            getJurisdictionAndStatus(personnel.id.toString()).then(response => {
                setJurisdictionInfo(response);
            });
        }
    }, [personnel]);

    useEffect(() => {
        if (personnel?.id) {
            reloadDisclosureReviews(targetJurisdiction);
        }
    }, [personnel]);

    useEffect(() => {
        if (!currentJurisdiction?.jurisdiction && disclosureReviews.length === 0) {
            return;
        }
        if (!currentJurisdiction?.jurisdiction) {
            return;
        }
        dispatch({
            type: "updateModalProperties",
            headerText:
                "Jurisdiction: " +
                (currentJurisdiction.jurisdictionText || currentJurisdiction.jurisdiction),
        });

        let disclosureCount = 0;
        disclosureCount = currentJurisdiction.disclosures?.filter(
            (disclosure: { answer: any }) => disclosure.answer,
        ).length;

        setDisclosureCount(disclosureCount);

        setDisclosureComment(currentJurisdiction.disclosureComment);

        let reviewCount = 0;
        if (currentJurisdiction.review?.previousNames) {
            reviewCount++;
        }
        if (
            currentJurisdiction.review?.countriesLived &&
            currentJurisdiction.review.countriesLived.length > 0
        ) {
            reviewCount++;
        }
        if (currentJurisdiction.attachments && currentJurisdiction.attachments.length > 0) {
            reviewCount++; // Count attachments as one item if any exist
        }
        setReviewCount(reviewCount);
        setReviewComment(currentJurisdiction.reviewComment);

        if (
            active &&
            (!currentJurisdiction.disclosures || currentJurisdiction.disclosures.length === 0)
        ) {
            setActiveTab(REVIEW_TAB);
            return;
        }

        let targetTab = DISCLOSURE_TAB;

        if (currentJurisdiction.disclosurePending) {
            if (currentJurisdiction.disclosureAccepted && currentJurisdiction.reviewPending) {
                targetTab = REVIEW_TAB;
            } else {
                targetTab = DISCLOSURE_TAB;
            }
            setActiveTab(targetTab);
        } else if (currentJurisdiction.reviewPending) {
            if (currentJurisdiction.reviewAccepted && currentJurisdiction.disclosurePending) {
                targetTab = DISCLOSURE_TAB;
            } else {
                targetTab = REVIEW_TAB;
            }
            setActiveTab(targetTab);
        } else {
            // Do nothing, don't switch Tab
        }
    }, [currentJurisdiction, disclosureReviews.length]);

    const acceptDisclosure = () => {
        const statusChangeDTO = {
            country: currentJurisdiction.jurisdiction,
            eventName: "Submit Consent Disclosures",
            comment: disclosureComment,
        };
        moveStatus(personnel.id, statusChangeDTO)
            .then(() =>
                reloadDisclosureReviewsUntilSettled(
                    currentJurisdiction.jurisdiction,
                    "disclosurePending",
                ).then(() => {
                    if (callback && typeof callback === "function") {
                        callback();
                    }
                }),
            )
            .catch(error => {
                console.log(error);
                showAlert("Accepting disclosure failed", "danger");
            });
    };

    const acceptReviewContent = (personnelNames: any, aliasNameList: any) => {
        personnelNames["aliasList"] = aliasNameList;
        const statusChangeDTO = {
            country: currentJurisdiction.jurisdiction,
            eventName: "Submit Consent Reviews",
            comment: reviewComment,
            additionalInfo: JSON.stringify(personnelNames),
        };
        moveStatus(personnel.id, statusChangeDTO)
            .then(() =>
                reloadDisclosureReviewsUntilSettled(
                    currentJurisdiction.jurisdiction,
                    "reviewPending",
                ).then(() => {
                    if (callback && typeof callback === "function") {
                        callback();
                    }
                }),
            )
            .catch(error => {
                console.log(error);
                showAlert("Accepting consent failed", "danger");
            });
    };

    const handleTabSelect = (key: any) => {
        setActiveTab(key);
    };
    return (
        <BodyContent>
            <Container>
                <Row>
                    <Col md={7}>
                        <NameContent>
                            {personnel.lastName}, {personnel.firstName}
                        </NameContent>
                        <TitleContent>{personnel.title}</TitleContent>
                        <OrganizationContent>
                            {personnel.affiliation.orgAbbreviation} - {personnel.affiliation.name}
                        </OrganizationContent>
                    </Col>
                    <Col md={5}>
                        <Row>
                            <Col md={5}>Living</Col>
                            <Col md={7}>{jurisdictionInfo?.living}</Col>
                        </Row>
                        <Row>
                            <Col md={5}>Working</Col>
                            <Col md={7}>{jurisdictionInfo?.working}</Col>
                        </Row>
                        <Row>
                            <Col md={5}>Headquartered</Col>
                            <Col md={7}>{jurisdictionInfo?.headquartered}</Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col md={12}>
                        {disclosureReviews.length === 0 && targetJurisdiction && (
                            <div className="p-3 text-muted" style={{ fontSize: "14px" }}>
                                No review data for this jurisdiction.
                            </div>
                        )}
                        {disclosureReviews.length > 0 && (
                            <Tabs
                                id="disclosureReviewTabs"
                                activeKey={activeTab}
                                onSelect={handleTabSelect}
                            >
                                {currentJurisdiction?.disclosures?.length > 0 && (
                                    <Tab
                                        eventKey={DISCLOSURE_TAB}
                                        title={
                                            <span>
                                                {disclosureCount} Disclosure
                                                {!currentJurisdiction.disclosurePending ? (
                                                    <StyledIcon />
                                                ) : null}
                                            </span>
                                        }
                                    >
                                        <DisclosureTab
                                            currentJurisdiction={currentJurisdiction}
                                            disclosureComment={disclosureComment}
                                            setDisclosureComment={setDisclosureComment}
                                            acceptDisclosure={acceptDisclosure}
                                            readOnly={readOnly}
                                        />
                                    </Tab>
                                )}
                                {(() => {
                                    const showReviewTab =
                                        currentJurisdiction.review ||
                                        currentJurisdiction.attachments?.length > 0 ||
                                        (currentJurisdiction.international &&
                                            currentJurisdiction.reviewPending);

                                    const showReviewAcceptedIcon =
                                        currentJurisdiction.reviewAccepted === true ||
                                        (!currentJurisdiction.reviewPending &&
                                            currentJurisdiction.review);

                                    if (!showReviewTab) {
                                        return null;
                                    }

                                    return (
                                        <Tab
                                            eventKey={REVIEW_TAB}
                                            title={
                                                <span>
                                                    {reviewCount} Review Consent
                                                    {showReviewAcceptedIcon ? <StyledIcon /> : null}
                                                </span>
                                            }
                                        >
                                            <ReviewTab
                                                personnel={personnel}
                                                currentJurisdiction={currentJurisdiction}
                                                reviewComment={reviewComment}
                                                setReviewComment={setReviewComment}
                                                acceptReviewContent={acceptReviewContent}
                                                readOnly={readOnly}
                                            />
                                        </Tab>
                                    );
                                })()}
                            </Tabs>
                        )}
                    </Col>
                </Row>
            </Container>
        </BodyContent>
    );
};

const ReviewDisclosureModalFooter: React.FC<ReviewDisclosureModalBodyProps> = ({
    personnel,
    setDisclosureComment,
    setReviewComment,
    disclosureReviews,
    setDisclosureReviews,
    currentJurisdiction,
    setCurrentJurisdictionIndex,
    currentJurisdictionIndex,
    setCurrentJurisdiction,
    targetJurisdiction,
    callback,
    active,
}) => {
    const { closeModal } = useGlobalModal();
    const showAlert = useAlert();
    const readOnly = active && active.toUpperCase() === "INACTIVE" ? true : false;
    const [submitBgcLoading, setSubmitBgcLoading] = useState(false);

    const showSubmitBgc =
        !readOnly &&
        !!currentJurisdiction?.jurisdiction &&
        currentJurisdiction.international === false;

    const submitBgcEnabled =
        !!personnel?.id &&
        !currentJurisdiction?.disclosurePending &&
        !currentJurisdiction?.reviewPending;

    const handleSubmitBgc = () => {
        if (
            !personnel?.id ||
            !currentJurisdiction?.jurisdiction ||
            submitBgcLoading ||
            !submitBgcEnabled
        ) {
            return;
        }
        setSubmitBgcLoading(true);
        moveStatus(personnel.id, {
            country: currentJurisdiction.jurisdiction,
            eventName: "Submit to BGC",
        })
            .then(() => {
                showAlert("Submitted", "success");
                if (callback && typeof callback === "function") {
                    callback();
                }
            })
            .catch(() => {
                showAlert("Submit to BGC failed. Please contact BIGS Systems.", "danger");
            })
            .finally(() => {
                setSubmitBgcLoading(false);
            });
    };

    const handleCompletePdfDownload = () => {
        if (!currentJurisdiction?.jurisdiction || !personnel?.id) return;
        getCompleteConsentFormPdf(personnel.id, currentJurisdiction.jurisdiction)
            .then((blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `consent-form-${currentJurisdiction.jurisdiction}-complete.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                showAlert("Failed to download complete PDF.", "danger");
            });
    };

    const displayNextJurisdictionButton = (disclosureReviews: any[], readOnly: boolean) => {
        if (readOnly) {
            return false;
        }
        const nextIndex = (currentJurisdictionIndex + 1) % disclosureReviews.length;

        return (
            disclosureReviews[nextIndex].disclosurePending ||
            disclosureReviews[nextIndex].reviewPending
        );
    };

    const nextJurisdiction = (disclosureReviews: any[]) => {
        disclosureReviews[currentJurisdictionIndex] = currentJurisdiction;
        const nextIndex = (currentJurisdictionIndex + 1) % disclosureReviews.length;
        setCurrentJurisdiction(disclosureReviews[nextIndex]);
        setCurrentJurisdictionIndex(nextIndex);

        // Deep clone disclosureReviews
        const newDisclosureReviews = JSON.parse(JSON.stringify(disclosureReviews));
        setDisclosureReviews(newDisclosureReviews);
    };

    return (
        <div className="d-flex">
            <div>
                {disclosureReviews.length > 1 &&
                    displayNextJurisdictionButton(disclosureReviews, readOnly) && (
                        <OutlinedButtonStyled
                            width={142}
                            height={26}
                            disabled={disclosureReviews.length < 2}
                            onClick={event => {
                                event.stopPropagation();
                                nextJurisdiction(disclosureReviews);
                            }}
                        >
                            Next Jurisdiction
                        </OutlinedButtonStyled>
                    )}
            </div>
            <div className="pl-2">
                <OutlinedButtonStyled
                    width={142}
                    height={26}
                    disabled={!currentJurisdiction?.consentFormAvailable}
                    onClick={event => {
                        event.stopPropagation();
                        handleCompletePdfDownload();
                    }}
                >
                    Complete PDF
                </OutlinedButtonStyled>
            </div>
            {showSubmitBgc && (
                <div className="pl-2">
                    <PrimaryButtonStyled
                        width={142}
                        height={26}
                        disabled={submitBgcLoading || !submitBgcEnabled}
                        onClick={event => {
                            event.stopPropagation();
                            handleSubmitBgc();
                        }}
                    >
                        Submit BGC
                    </PrimaryButtonStyled>
                </div>
            )}
            <div className="pl-2">
                <OutlinedButtonStyled
                    width={142}
                    height={26}
                    disabled={false}
                    onClick={event => {
                        event.stopPropagation();
                        setDisclosureComment("");
                        setReviewComment("");
                        if (callback && typeof callback === "function") {
                            callback();
                        }
                        closeModal();
                    }}
                >
                    Close
                </OutlinedButtonStyled>
            </div>
        </div>
    );
};

ReviewDisclosureModalBody.displayName = "BodyContent";
ReviewDisclosureModalFooter.displayName = "FooterContent";

export { ReviewDisclosureModalBody, ReviewDisclosureModalFooter };
